/**
 * Catálogo estático de idiomas do Módulo de Idiomas (paridade com a lista GTranslate, BCP-47).
 *
 * Dois níveis:
 *  - `LANGUAGE_SCHEMA_SUPERSET` — locales declarados no `payload.config` (limite técnico:
 *    `localization.locales` é build-time). Só estes podem ter conteúdo traduzido.
 *  - `LANGUAGE_CATALOG` — todos os idiomas conhecidos (~100), p/ UI do admin; um idioma fora do
 *    superset aparece com aviso «requer atualização do site» (expandir = 1 linha + migration).
 *
 * `flag` = código de país ISO-3166 alpha-2 do flagcdn.com (já pré-conectado no layout público).
 */

export type LanguageCatalogEntry = {
  /** Código BCP-47 usado como locale do Payload e prefixo de URL (`/en/...`). */
  code: string
  /** Nome em português (admin). */
  name: string
  /** Nome nativo (seletor público). */
  nativeName: string
  /** País da bandeira (flagcdn). */
  flag: string
  /** Escrita direita→esquerda. */
  rtl?: boolean
}

/** Locales declarados no schema do Payload — ordem: default primeiro. */
export const LANGUAGE_SCHEMA_SUPERSET = [
  'pt',
  'en',
  'es',
  'it',
  'fr',
  'de',
  'tr',
  'nl',
  'ko',
  'pl',
  'id',
  'ja',
  'zh-CN',
  'ar',
  'ru',
  'hi',
  'th',
  'vi',
] as const

export type SchemaLocale = (typeof LANGUAGE_SCHEMA_SUPERSET)[number]

export const DEFAULT_LOCALE: SchemaLocale = 'pt'

/**
 * FASE 3 (spec CORRECAO_TRADUCAO_EN_GATE_JA, C1): locales cuja escrita NÃO é latina. `slugifyText` só
 * preserva a-z/0-9 (NFD + strip de marcas de combinação, SEM transliteração) → para estes o slug do
 * texto traduzido vem VAZIO (CJK/Árabe/Cirílico/Devanágari/Tailandês) ou vira fragmento latino solto
 * (nº, marcas). Decisão do dono: nestes idiomas o slug localizado usa o slug EN (determinístico e
 * legível, sem repetir o slug PT cru). `vi/pl/id/tr/de/nl` são latinos-com-diacríticos → transliteram OK.
 */
const NON_LATIN_SCRIPT_LOCALES: ReadonlySet<string> = new Set([
  'ko',
  'ja',
  'zh-CN',
  'ar',
  'ru',
  'hi',
  'th',
])

/** true se o locale usa escrita não-latina (ver `NON_LATIN_SCRIPT_LOCALES`). */
export function isNonLatinScriptLocale(locale: string | null | undefined): boolean {
  return !!locale && NON_LATIN_SCRIPT_LOCALES.has(locale)
}

export const LANGUAGE_CATALOG: LanguageCatalogEntry[] = [
  { code: 'pt', name: 'Português', nativeName: 'Português', flag: 'br' },
  { code: 'en', name: 'Inglês', nativeName: 'English', flag: 'us' },
  { code: 'es', name: 'Espanhol', nativeName: 'Español', flag: 'es' },
  { code: 'it', name: 'Italiano', nativeName: 'Italiano', flag: 'it' },
  { code: 'fr', name: 'Francês', nativeName: 'Français', flag: 'fr' },
  { code: 'de', name: 'Alemão', nativeName: 'Deutsch', flag: 'de' },
  { code: 'tr', name: 'Turco', nativeName: 'Türkçe', flag: 'tr' },
  { code: 'nl', name: 'Holandês', nativeName: 'Nederlands', flag: 'nl' },
  { code: 'ko', name: 'Coreano', nativeName: '한국어', flag: 'kr' },
  { code: 'pl', name: 'Polonês', nativeName: 'Polski', flag: 'pl' },
  { code: 'id', name: 'Indonésio', nativeName: 'Bahasa Indonesia', flag: 'id' },
  { code: 'ja', name: 'Japonês', nativeName: '日本語', flag: 'jp' },
  { code: 'zh-CN', name: 'Chinês (simplificado)', nativeName: '简体中文', flag: 'cn' },
  { code: 'ar', name: 'Árabe', nativeName: 'العربية', flag: 'sa', rtl: true },
  { code: 'ru', name: 'Russo', nativeName: 'Русский', flag: 'ru' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: 'in' },
  { code: 'th', name: 'Tailandês', nativeName: 'ไทย', flag: 'th' },
  { code: 'vi', name: 'Vietnamita', nativeName: 'Tiếng Việt', flag: 'vn' },
  // ── Fora do superset (catálogo p/ futuro; «requer atualização do site») ──────
  { code: 'af', name: 'Africâner', nativeName: 'Afrikaans', flag: 'za' },
  { code: 'sq', name: 'Albanês', nativeName: 'Shqip', flag: 'al' },
  { code: 'am', name: 'Amárico', nativeName: 'አማርኛ', flag: 'et' },
  { code: 'hy', name: 'Armênio', nativeName: 'Հայերեն', flag: 'am' },
  { code: 'az', name: 'Azerbaijano', nativeName: 'Azərbaycanca', flag: 'az' },
  { code: 'eu', name: 'Basco', nativeName: 'Euskara', flag: 'es' },
  { code: 'be', name: 'Bielorrusso', nativeName: 'Беларуская', flag: 'by' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: 'bd' },
  { code: 'bs', name: 'Bósnio', nativeName: 'Bosanski', flag: 'ba' },
  { code: 'bg', name: 'Búlgaro', nativeName: 'Български', flag: 'bg' },
  { code: 'ca', name: 'Catalão', nativeName: 'Català', flag: 'es' },
  { code: 'ceb', name: 'Cebuano', nativeName: 'Cebuano', flag: 'ph' },
  { code: 'ny', name: 'Chichewa', nativeName: 'Chichewa', flag: 'mw' },
  { code: 'zh-TW', name: 'Chinês (tradicional)', nativeName: '繁體中文', flag: 'tw' },
  { code: 'co', name: 'Corso', nativeName: 'Corsu', flag: 'fr' },
  { code: 'hr', name: 'Croata', nativeName: 'Hrvatski', flag: 'hr' },
  { code: 'cs', name: 'Tcheco', nativeName: 'Čeština', flag: 'cz' },
  { code: 'da', name: 'Dinamarquês', nativeName: 'Dansk', flag: 'dk' },
  { code: 'eo', name: 'Esperanto', nativeName: 'Esperanto', flag: 'eu' },
  { code: 'et', name: 'Estoniano', nativeName: 'Eesti', flag: 'ee' },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino', flag: 'ph' },
  { code: 'fi', name: 'Finlandês', nativeName: 'Suomi', flag: 'fi' },
  { code: 'fy', name: 'Frísio', nativeName: 'Frysk', flag: 'nl' },
  { code: 'gd', name: 'Gaélico escocês', nativeName: 'Gàidhlig', flag: 'gb' },
  { code: 'gl', name: 'Galego', nativeName: 'Galego', flag: 'es' },
  { code: 'cy', name: 'Galês', nativeName: 'Cymraeg', flag: 'gb' },
  { code: 'ka', name: 'Georgiano', nativeName: 'ქართული', flag: 'ge' },
  { code: 'el', name: 'Grego', nativeName: 'Ελληνικά', flag: 'gr' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: 'in' },
  { code: 'ha', name: 'Hauçá', nativeName: 'Hausa', flag: 'ng' },
  { code: 'haw', name: 'Havaiano', nativeName: 'ʻŌlelo Hawaiʻi', flag: 'us' },
  { code: 'iw', name: 'Hebraico', nativeName: 'עברית', flag: 'il', rtl: true },
  { code: 'hmn', name: 'Hmong', nativeName: 'Hmoob', flag: 'cn' },
  { code: 'hu', name: 'Húngaro', nativeName: 'Magyar', flag: 'hu' },
  { code: 'ig', name: 'Igbo', nativeName: 'Igbo', flag: 'ng' },
  { code: 'yi', name: 'Iídiche', nativeName: 'ייִדיש', flag: 'il', rtl: true },
  { code: 'ga', name: 'Irlandês', nativeName: 'Gaeilge', flag: 'ie' },
  { code: 'is', name: 'Islandês', nativeName: 'Íslenska', flag: 'is' },
  { code: 'jw', name: 'Javanês', nativeName: 'Basa Jawa', flag: 'id' },
  { code: 'kn', name: 'Canarês', nativeName: 'ಕನ್ನಡ', flag: 'in' },
  { code: 'kk', name: 'Cazaque', nativeName: 'Қазақша', flag: 'kz' },
  { code: 'km', name: 'Khmer', nativeName: 'ខ្មែរ', flag: 'kh' },
  { code: 'ku', name: 'Curdo (Kurmanji)', nativeName: 'Kurdî', flag: 'iq' },
  { code: 'ky', name: 'Quirguiz', nativeName: 'Кыргызча', flag: 'kg' },
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', flag: 'la' },
  { code: 'la', name: 'Latim', nativeName: 'Latina', flag: 'va' },
  { code: 'lv', name: 'Letão', nativeName: 'Latviešu', flag: 'lv' },
  { code: 'lt', name: 'Lituano', nativeName: 'Lietuvių', flag: 'lt' },
  { code: 'lb', name: 'Luxemburguês', nativeName: 'Lëtzebuergesch', flag: 'lu' },
  { code: 'mk', name: 'Macedônio', nativeName: 'Македонски', flag: 'mk' },
  { code: 'ml', name: 'Malaiala', nativeName: 'മലയാളം', flag: 'in' },
  { code: 'ms', name: 'Malaio', nativeName: 'Bahasa Melayu', flag: 'my' },
  { code: 'mg', name: 'Malgaxe', nativeName: 'Malagasy', flag: 'mg' },
  { code: 'mt', name: 'Maltês', nativeName: 'Malti', flag: 'mt' },
  { code: 'mi', name: 'Maori', nativeName: 'Māori', flag: 'nz' },
  { code: 'mr', name: 'Marati', nativeName: 'मराठी', flag: 'in' },
  { code: 'my', name: 'Birmanês', nativeName: 'မြန်မာစာ', flag: 'mm' },
  { code: 'mn', name: 'Mongol', nativeName: 'Монгол', flag: 'mn' },
  { code: 'ne', name: 'Nepalês', nativeName: 'नेपाली', flag: 'np' },
  { code: 'no', name: 'Norueguês', nativeName: 'Norsk', flag: 'no' },
  { code: 'ps', name: 'Pachto', nativeName: 'پښتو', flag: 'af', rtl: true },
  { code: 'fa', name: 'Persa', nativeName: 'فارسی', flag: 'ir', rtl: true },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: 'in' },
  { code: 'ro', name: 'Romeno', nativeName: 'Română', flag: 'ro' },
  { code: 'sm', name: 'Samoano', nativeName: 'Gagana Samoa', flag: 'ws' },
  { code: 'sr', name: 'Sérvio', nativeName: 'Српски', flag: 'rs' },
  { code: 'st', name: 'Sesoto', nativeName: 'Sesotho', flag: 'ls' },
  { code: 'sn', name: 'Shona', nativeName: 'ChiShona', flag: 'zw' },
  { code: 'sd', name: 'Sindi', nativeName: 'سنڌي', flag: 'pk', rtl: true },
  { code: 'si', name: 'Cingalês', nativeName: 'සිංහල', flag: 'lk' },
  { code: 'sk', name: 'Eslovaco', nativeName: 'Slovenčina', flag: 'sk' },
  { code: 'sl', name: 'Esloveno', nativeName: 'Slovenščina', flag: 'si' },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: 'so' },
  { code: 'sw', name: 'Suaíli', nativeName: 'Kiswahili', flag: 'ke' },
  { code: 'sv', name: 'Sueco', nativeName: 'Svenska', flag: 'se' },
  { code: 'su', name: 'Sundanês', nativeName: 'Basa Sunda', flag: 'id' },
  { code: 'tg', name: 'Tadjique', nativeName: 'Тоҷикӣ', flag: 'tj' },
  { code: 'ta', name: 'Tâmil', nativeName: 'தமிழ்', flag: 'in' },
  { code: 'te', name: 'Télugo', nativeName: 'తెలుగు', flag: 'in' },
  { code: 'uk', name: 'Ucraniano', nativeName: 'Українська', flag: 'ua' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: 'pk', rtl: true },
  { code: 'uz', name: 'Uzbeque', nativeName: "Oʻzbekcha", flag: 'uz' },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', flag: 'za' },
  { code: 'yo', name: 'Iorubá', nativeName: 'Yorùbá', flag: 'ng' },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: 'za' },
]

const byCode = new Map(LANGUAGE_CATALOG.map((l) => [l.code.toLowerCase(), l]))

export function getCatalogEntry(code: string): LanguageCatalogEntry | null {
  return byCode.get(code.trim().toLowerCase()) ?? null
}

export function isSchemaLocale(code: string): code is SchemaLocale {
  return (LANGUAGE_SCHEMA_SUPERSET as readonly string[]).includes(code)
}

export function isRtlLocale(code: string): boolean {
  return Boolean(getCatalogEntry(code)?.rtl)
}

/** URL da bandeira (svg, flagcdn — domínio já pré-conectado no layout). */
export function flagUrl(entry: Pick<LanguageCatalogEntry, 'flag'>, size: 'w20' | 'w40' = 'w20'): string {
  return `https://flagcdn.com/${size}/${entry.flag}.png`
}

/**
 * Prefixos de URL que o proxy reconhece como idioma (todos os códigos do catálogo, minúsculos).
 * Inclui códigos fora do superset: URLs antigas do GTranslate (site WP) usam qualquer um deles
 * e devem voltar 301 ao caminho canónico pt.
 */
export const LOCALE_URL_PREFIXES: ReadonlySet<string> = new Set(
  LANGUAGE_CATALOG.map((l) => l.code.toLowerCase()).filter((c) => c !== DEFAULT_LOCALE),
)
